/**
 * Iframe resize handler factory - creates postMessage listeners for iframe height adjustment
 * @module lib/utils/iframeResizer
 */

/**
 * Create an iframe resizer function for a specific message type
 * @param {object} options - Configuration options
 * @param {string} options.messageType - The postMessage type to listen for (e.g., 'MILLA_NEWSLETTER_RESIZE')
 * @param {number} [options.minHeight=180] - Minimum allowed height in pixels
 * @param {number} [options.maxHeight=800] - Maximum allowed height in pixels
 * @param {string} [options.scrollMessageType] - Optional postMessage type for scroll requests
 * @returns {function} A function that sets up the resize listener (idempotent)
 */
function createIframeResizer(options) {
  const {
    messageType,
    minHeight = 180,
    maxHeight = 800,
    scrollMessageType
  } = options;

  if (!messageType || typeof messageType !== 'string') {
    throw new Error('createIframeResizer requires a messageType string');
  }

  let initialized = false;

  return function ensureIframeResizer() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    if (initialized) {
      return;
    }

    window.addEventListener('message', (event) => {
      const data = event && event.data;
      if (!data || typeof data !== 'object') return;
      const isResize = data.type === messageType;
      const isScroll = scrollMessageType && data.type === scrollMessageType;
      if (!isResize && !isScroll) return;

      const iframes = Array.from(document.getElementsByTagName('iframe'));
      const target = iframes.find((iframe) => iframe && iframe.contentWindow === event.source);
      if (!target) return;

      if (isResize) {
        const height = Number(data.height);
        if (!Number.isFinite(height)) return;

        // Clamp height to safe bounds
        const clampedHeight = Math.max(minHeight, Math.min(maxHeight, Math.ceil(height)));

        target.style.height = `${clampedHeight}px`;
        const wrapper = target.parentElement;
        if (wrapper && wrapper.style) {
          wrapper.style.minHeight = `${clampedHeight}px`;
        }
      }

      if (isScroll) {
        try {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (err) {
          target.scrollIntoView(true);
        }
      }
    });

    initialized = true;
  };
}

module.exports = { createIframeResizer };
